import { useState, useRef, useEffect, useCallback } from 'react';
import { 
  MessageSquare, 
  X, 
  Send, 
  Sparkles, 
  ChevronDown,
  User,
  Globe,
  Sun,
  Shield,
  FileText,
  RotateCcw
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { 
  streamChat, 
  getStoredIdentity, 
  setStoredIdentity,
  getIdentities,
  getSession,
  updateSession,
  getFallbackWelcome,
  checkHealth,
  type Identity,
  type ChatContext
} from '@/services/api';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  identity?: string;
}

const identityConfig: Record<string, { icon: React.ElementType; color: string; bgColor: string }> = {
  leroy: { icon: Globe, color: 'text-[#BDD630]', bgColor: 'bg-[#BDD630]' },
  olufemi: { icon: Sun, color: 'text-[#F4C95D]', bgColor: 'bg-[#F4C95D]' },
  tshiamo: { icon: Shield, color: 'text-[#8B9D83]', bgColor: 'bg-[#8B9D83]' }
};

export function LeroyChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [identity, setIdentity] = useState<'leroy' | 'olufemi' | 'tshiamo'>(getStoredIdentity());
  const [showIdentitySelector, setShowIdentitySelector] = useState(false);
  const [identities, setIdentities] = useState<Identity[]>([]);
  const [threadId] = useState(() => `thread_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [hasGreeted, setHasGreeted] = useState(false);
  const [isFirstOpen, setIsFirstOpen] = useState(true);
  const [serverStatus, setServerStatus] = useState<'checking' | 'online' | 'offline'>('checking');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const streamAbortRef = useRef<AbortController | null>(null);

  // Load identities on mount — handle errors so component doesn't silently fail
  useEffect(() => {
    Promise.all([getIdentities(), checkHealth()])
      .then(([ids, health]) => {
        setIdentities(ids);
        setServerStatus(health.status === 'healthy' ? 'online' : 'offline');
      })
      .catch(err => {
        console.error('[LeroyChat] Failed to load initial data:', err);
        setServerStatus('offline');
      });
  }, []);

  // Abort any in-flight stream when component unmounts
  useEffect(() => {
    return () => {
      streamAbortRef.current?.abort();
    };
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Allow other parts of the app to open the chat via a custom event
  useEffect(() => {
    const handleOpenRequest = () => setIsOpen(true);
    window.addEventListener('open-leroy-chat', handleOpenRequest);
    return () => window.removeEventListener('open-leroy-chat', handleOpenRequest);
  }, []);

  // Handle first open greeting
  useEffect(() => {
    if (isOpen && isFirstOpen && !hasGreeted) {
      setIsFirstOpen(false);
      setHasGreeted(true);
      
      // Send welcome message after a brief delay
      setTimeout(() => {
        const welcomeMessage = getFallbackWelcome(identity);
        setMessages([{
          role: 'assistant',
          content: welcomeMessage,
          timestamp: new Date(),
          identity
        }]);
      }, 500);
    }
  }, [isOpen, isFirstOpen, hasGreeted, identity]);

  // Handle identity change
  const handleIdentityChange = useCallback(async (newIdentity: 'leroy' | 'olufemi' | 'tshiamo') => {
    setIdentity(newIdentity);
    setStoredIdentity(newIdentity);
    setShowIdentitySelector(false);
    
    // Update session on server
    await updateSession(threadId, { identity: newIdentity });
    
    // Add system message about identity change
    const identityNames: Record<string, string> = {
      leroy: 'Leroy',
      olufemi: 'Olufemi',
      tshiamo: 'Tshiamo'
    };
    
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: `I'm now speaking as ${identityNames[newIdentity]}. How can I help you today?`,
      timestamp: new Date(),
      identity: newIdentity
    }]);
  }, [threadId]);

  // Handle sending message
  const handleSend = useCallback(async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setIsLoading(true);

    // Add user message
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    }]);

    // Abort any previous in-flight stream before starting a new one
    streamAbortRef.current?.abort();
    const abortController = new AbortController();
    streamAbortRef.current = abortController;

    try {
      const context: ChatContext = {
        page: window.location.pathname,
        identity,
        userId: threadId
      };

      let fullResponse = '';

      // Add placeholder for assistant response
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        identity
      }]);

      for await (const chunk of streamChat(userMessage, context, threadId, abortController.signal)) {
        // Stop processing if the stream was aborted (component unmounted)
        if (abortController.signal.aborted) break;
        if (chunk.content) {
          fullResponse += chunk.content;
          setMessages(prev => {
            const updated = [...prev];
            const lastMessage = updated[updated.length - 1];
            if (lastMessage.role === 'assistant') {
              lastMessage.content = fullResponse;
            }
            return updated;
          });
        }
        if (chunk.error) {
          throw new Error(chunk.error);
        }
      }

    } catch (error) {
      // Ignore abort errors — they are intentional (unmount or new message)
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error('Chat error:', error);
      
      // Add error message
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I apologize, but I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: new Date(),
        identity
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, identity, threadId]);

  // Handle key press
  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  // Clear chat
  const handleClearChat = useCallback(() => {
    setMessages([]);
    setHasGreeted(false);
    setIsFirstOpen(true);
  }, []);

  // Get current identity config
  const currentConfig = identityConfig[identity];
  const CurrentIcon = currentConfig.icon;

  return (
    <>
      {/* Floating Chat Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={cn(
          "fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg",
          "flex items-center justify-center",
          "transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-4 focus:ring-[#BDD630]/50",
          currentConfig.bgColor,
          isOpen ? "scale-0 opacity-0" : "scale-100 opacity-100"
        )}
        aria-label={`Open chat with ${identities.find(i => i.id === identity)?.name || 'Leroy'}`}
        aria-expanded={isOpen}
        aria-controls="leroy-chat-window"
      >
        <CurrentIcon className="w-6 h-6 text-[#1A2E12]" aria-hidden="true" />
      </button>

      {/* Chat Window */}
      <div
        id="leroy-chat-window"
        role="dialog"
        aria-label="Chat with Marple skincare assistant"
        aria-modal="true"
        className={cn(
          "fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)]",
          "bg-[#1A2E12] rounded-2xl shadow-2xl overflow-hidden",
          "transition-all duration-300 origin-bottom-right",
          "border border-[#F8F7F2]/10",
          isOpen 
            ? "scale-100 opacity-100 translate-y-0" 
            : "scale-0 opacity-0 translate-y-4 pointer-events-none"
        )}
      >
        {/* Header */}
        <div className={cn("p-4 border-b border-[#F8F7F2]/10", currentConfig.bgColor)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#1A2E12]/20 flex items-center justify-center" aria-hidden="true">
                <CurrentIcon className="w-5 h-5 text-[#1A2E12]" />
              </div>
              <div>
                <h3 className="font-semibold text-[#1A2E12]">
                  {identities.find(i => i.id === identity)?.name || 'Leroy'}
                </h3>
                <p className="text-xs text-[#1A2E12]/70" role="status" aria-live="polite">
                  {serverStatus === 'online' ? 'Online' : 'Connecting...'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setShowIdentitySelector(!showIdentitySelector)}
                className="p-2 rounded-lg hover:bg-[#1A2E12]/10 transition-colors focus:outline-none focus:ring-2 focus:ring-[#1A2E12]/30"
                aria-label="Switch skincare guide"
                aria-expanded={showIdentitySelector}
                aria-controls="identity-selector"
              >
                <RotateCcw className="w-4 h-4 text-[#1A2E12]" aria-hidden="true" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-lg hover:bg-[#1A2E12]/10 transition-colors focus:outline-none focus:ring-2 focus:ring-[#1A2E12]/30"
                aria-label="Close chat"
              >
                <X className="w-5 h-5 text-[#1A2E12]" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Identity Selector */}
          {showIdentitySelector && (
            <div 
              id="identity-selector"
              role="radiogroup"
              aria-label="Select skincare guide"
              className="mt-3 p-3 bg-[#1A2E12]/10 rounded-xl"
            >
              <p className="text-xs font-medium text-[#1A2E12]/70 mb-2 uppercase tracking-wider">
                Choose your skincare guide
              </p>
              <div className="space-y-2">
                {identities.map((id) => {
                  const IdIcon = identityConfig[id.id].icon;
                  const isSelected = identity === id.id;
                  
                  return (
                    <button
                      key={id.id}
                      onClick={() => handleIdentityChange(id.id as typeof identity)}
                      role="radio"
                      aria-checked={isSelected}
                      className={cn(
                        "w-full flex items-center gap-3 p-2 rounded-lg transition-all",
                        "text-left hover:bg-[#1A2E12]/20 focus:outline-none focus:ring-2 focus:ring-[#1A2E12]/30",
                        isSelected && "bg-[#1A2E12]/20 ring-1 ring-[#1A2E12]/30"
                      )}
                    >
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: id.color }}
                        aria-hidden="true"
                      >
                        <IdIcon className="w-4 h-4 text-[#1A2E12]" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-[#1A2E12] text-sm">
                          {id.name}
                        </p>
                        <p className="text-xs text-[#1A2E12]/70">
                          {id.title}
                        </p>
                      </div>
                      {isSelected && (
                        <div className="w-2 h-2 rounded-full bg-[#1A2E12]" aria-hidden="true" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Messages */}
        <div 
          ref={chatContainerRef}
          role="log"
          aria-live="polite"
          aria-label="Chat messages"
          className="h-[350px] overflow-y-auto p-4 space-y-4 bg-[#1A2E12]"
        >
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-6">
              <div className={cn(
                "w-16 h-16 rounded-full flex items-center justify-center mb-4",
                currentConfig.bgColor
              )} aria-hidden="true">
                <Sparkles className="w-8 h-8 text-[#1A2E12]" />
              </div>
              <p className="text-[#F8F7F2]/60 text-sm">
                Your skincare companion is ready to help
              </p>
              <p className="text-[#F8F7F2]/40 text-xs mt-2">
                Ask about products, routines, or skin concerns
              </p>
            </div>
          ) : (
            messages.map((message, index) => (
              <div
                key={index}
                className={cn(
                  "flex gap-3",
                  message.role === 'user' ? "justify-end" : "justify-start"
                )}
              >
                {message.role === 'assistant' && (
                  <div className={cn(
                    "w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center",
                    currentConfig.bgColor
                  )} aria-hidden="true">
                    <CurrentIcon className="w-4 h-4 text-[#1A2E12]" />
                  </div>
                )}
                <div
                  role={message.role === 'assistant' ? "article" : undefined}
                  aria-label={message.role === 'assistant' ? `${identities.find(i => i.id === message.identity)?.name || 'Assistant'} message` : undefined}
                  className={cn(
                    "max-w-[75%] p-3 rounded-2xl text-sm",
                    message.role === 'user'
                      ? "bg-[#F8F7F2] text-[#1A2E12] rounded-br-md"
                      : "bg-[#F8F7F2]/10 text-[#F8F7F2] rounded-bl-md"
                  )}
                >
                  {message.content || (
                    <span className="flex items-center gap-1" aria-label="Typing" role="status">
                      <span className="w-2 h-2 rounded-full bg-current animate-bounce" aria-hidden="true" />
                      <span className="w-2 h-2 rounded-full bg-current animate-bounce delay-75" aria-hidden="true" />
                      <span className="w-2 h-2 rounded-full bg-current animate-bounce delay-150" aria-hidden="true" />
                    </span>
                  )}
                </div>
                {message.role === 'user' && (
                  <div className="w-8 h-8 rounded-full bg-[#F8F7F2]/20 flex-shrink-0 flex items-center justify-center" aria-hidden="true">
                    <User className="w-4 h-4 text-[#F8F7F2]" />
                  </div>
                )}
              </div>
            ))
          )}
          <div ref={messagesEndRef} aria-hidden="true" />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-[#F8F7F2]/10 bg-[#1A2E12]">
          <form 
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              handleSend();
            }}
          >
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              disabled={isLoading}
              aria-label="Type your message"
              aria-busy={isLoading}
              className={cn(
                "flex-1 px-4 py-2 rounded-xl text-sm",
                "bg-[#F8F7F2]/10 text-[#F8F7F2] placeholder:text-[#F8F7F2]/40",
                "border border-transparent focus:border-[#F8F7F2]/20",
                "outline-none transition-colors focus:ring-2 focus:ring-[#F8F7F2]/20",
                isLoading && "opacity-50 cursor-not-allowed"
              )}
            />
            <button
              type="submit"
              onClick={handleSend}
              disabled={isLoading || !input.trim()}
              aria-label="Send message"
              aria-busy={isLoading}
              className={cn(
                "px-4 py-2 rounded-xl transition-all",
                currentConfig.bgColor,
                "disabled:opacity-50 disabled:cursor-not-allowed",
                "hover:brightness-110 active:scale-95",
                "focus:outline-none focus:ring-2 focus:ring-[#F8F7F2]/30"
              )}
            >
              <Send className="w-4 h-4 text-[#1A2E12]" aria-hidden="true" />
            </button>
          </form>
          
          {/* Footer actions */}
          <div className="flex items-center justify-between mt-3">
            <button
              onClick={handleClearChat}
              className="text-xs text-[#F8F7F2]/40 hover:text-[#F8F7F2]/60 transition-colors focus:outline-none focus:ring-2 focus:ring-[#F8F7F2]/20 rounded px-2 py-1"
            >
              Clear conversation
            </button>
            <button
              onClick={() => setShowIdentitySelector(!showIdentitySelector)}
              aria-expanded={showIdentitySelector}
              aria-controls="identity-selector"
              className="text-xs text-[#F8F7F2]/40 hover:text-[#F8F7F2]/60 transition-colors focus:outline-none focus:ring-2 focus:ring-[#F8F7F2]/20 rounded px-2 py-1"
            >
              Switch guide
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default LeroyChat;
